import { useMemo } from 'react'

import { IconCopy, IconTrash } from '@tabler/icons-react'
import styled from 'styled-components'

import {
  deleteItem,
  dynamicClone,
  findItem,
  insertItemAt,
} from '../templateEditor/editorHelper'
import { Block, BlockGroup, Section, TemplateData } from '../types/blueprint'

import { CaptureBlockActionButton } from './CaptureBlock.style'
import { generateId } from './utils/generateTemplateIds'

import { useTemplateRenderer } from '@/context/templateRendererContext'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0 0.25rem;

  ${({ theme: { media } }) => media.md`
    flex-grow: 1;
  `}
`

const getClone = (item: Props['item'], type: Props['type']) => {
  if (type === 'BLOCK_GROUP') {
    const blockGroup = dynamicClone(type, item) as BlockGroup
    // Create a mapping of old block IDs to new block IDs for condition updates
    const idMapping: Record<string, string> = {}

    // Generate new IDs for blocks and store the mapping
    const newBlocks = blockGroup.blocks?.map((b) => {
      const oldId = b.id || ''
      const newId = generateId('BLOCK', { kind: b.kind, type: b.type })
      idMapping[oldId] = newId

      return {
        ...b,
        ...(b.repeatable && { repeatOfId: b.repeatOfId ?? b.id }), // keep the original block id
        id: newId,
      }
    })

    // Update any conditions that reference block IDs within this group
    const blocksWithUpdatedConditions = newBlocks?.map((block) => {
      if (block.conditions) {
        return {
          ...block,
          conditions: block.conditions.map((conditionGroup) =>
            conditionGroup.map((condition) => {
              if (condition.blockId && idMapping[condition.blockId]) {
                return {
                  ...condition,
                  blockId: idMapping[condition.blockId],
                }
              }
              return condition
            })
          ),
        }
      }
      return block
    })

    return {
      ...blockGroup,
      repeatOfId: blockGroup.id,
      id: generateId('BLOCK_GROUP'),
      blocks: blocksWithUpdatedConditions,
    } as BlockGroup
  }

  if (type === 'SECTION') {
    const section = dynamicClone(type, item) as Section
    // Create a mapping of old IDs to new IDs for condition updates
    const idMapping: Record<string, string> = {}

    // Generate new IDs for block groups and their blocks
    const newBlockGroups = section.blockGroups?.map((bg) => {
      const oldId = bg.id || ''
      const newId = generateId('BLOCK_GROUP')
      idMapping[oldId] = newId

      // Generate new IDs for blocks and store the mapping
      const newBlocks = bg.blocks?.map((b) => {
        const oldBlockId = b.id || ''
        const newBlockId = generateId('BLOCK', { kind: b.kind, type: b.type })
        idMapping[oldBlockId] = newBlockId

        return {
          ...b,
          ...(b.repeatable && { repeatOfId: b.repeatOfId ?? b.id }), // keep the original block id
          id: newBlockId,
        }
      })

      // Update any conditions that reference block IDs within this group
      const blocksWithUpdatedConditions = newBlocks?.map((block) => {
        if (block.conditions) {
          return {
            ...block,
            conditions: block.conditions.map((conditionGroup) =>
              conditionGroup.map((condition) => {
                if (condition.blockId && idMapping[condition.blockId]) {
                  return {
                    ...condition,
                    blockId: idMapping[condition.blockId],
                  }
                }
                return condition
              })
            ),
          }
        }
        return block
      })

      return {
        ...bg,
        ...(bg.repeatable && { repeatOfId: bg.repeatOfId ?? bg.id }),
        id: newId,
        blocks: blocksWithUpdatedConditions,
      }
    })

    return {
      ...section,
      repeatOfId: section.id,
      id: generateId('SECTION'),
      blockGroups: newBlockGroups,
    } as Section
  }

  const block = dynamicClone('BLOCK', item)
  return {
    ...block,
    repeatOfId: block.repeatOfId ?? block.id,
    id: generateId('BLOCK', { kind: block.kind, type: block.type }),
  } as Block
}

const getName = (name: string, index: number) => {
  if (!name?.trim()?.length) return `(${index})`

  return `${name} (${index})`
}

const getRepeatOfIds = (
  parent: Section | BlockGroup | TemplateData,
  type: Props['type']
) => {
  if (type === 'BLOCK') {
    const blockGroup = parent as BlockGroup
    return blockGroup.blocks.map((b) => b.repeatOfId ?? b.id)
  }
  if (type === 'BLOCK_GROUP') {
    const section = parent as Section
    return section.blockGroups.map((bg) => bg.repeatOfId ?? bg.id)
  }
  const template = parent as TemplateData
  return template.sections.map((s) => s.repeatOfId ?? s.id)
}

type Props = {
  item: Block | BlockGroup | Section
  type: 'BLOCK' | 'BLOCK_GROUP' | 'SECTION'
  parentId: string
  parent?: TemplateData | Section | BlockGroup
}

/**
 * Hi there developer. If you are looking at this file, it is likely because you are expecting the "Add another" button
 * to render but for some reason it isn't and Matt/Gilad is not happy about it. Classic Matt and Gilad...
 * Anywho, here are some common reasons the button might not be rendering:
 *
 * 1. The original template is not supplied. We need access to the original template to clone from. This is because we need the block
 * without any values, just the skeleton. Make sure we are fetching the original template as part of the graphQL query.
 *
 * 2. The block is not in the original template. Maybe they are out of sync. Unsure, but we need to be able to find the original block
 * from the original template in order to clone it.
 *
 * 3. The block is not repeatable. Toggle that bad boi on in the template editor.
 *
 * 4. In the case of a block group, the group needs to have a name. We can fix this but it's currently the requirement.
 *
 * If it's none of those, then I don't know what to tell you. Good luck.
 */
export const AddAnother: React.FC<Props> = ({
  item,
  type,
  parentId,
  parent,
}) => {
  const { templateResponseData, originalTemplateData, setTemplateData } =
    useTemplateRenderer()

  const originalItemId = useMemo(() => item.repeatOfId ?? item.id, [item])

  const originalItem = useMemo(() => {
    if (!originalTemplateData) return null

    return findItem(type, originalItemId, originalTemplateData)
  }, [originalItemId, originalTemplateData, type])

  const showAddAnother = useMemo(() => {
    return item.repeatable && !!originalTemplateData && !!originalItem
  }, [item, originalTemplateData, originalItem])

  const showRemove = useMemo(() => {
    return originalItem?.repeatable && !item.repeatable
  }, [originalItem?.repeatable, item.repeatable])

  const onAddAnother = () => {
    let parentObj = parent
    if (!parentObj) {
      parentObj = findItem(
        type === 'BLOCK' ? 'BLOCK_GROUP' : 'SECTION',
        parentId,
        templateResponseData
      )
    }

    const repeatOfIds = getRepeatOfIds(parentObj, type) // find all the repeatOfIds and including this block's id
    const lastOccurance = repeatOfIds.lastIndexOf(originalItemId) // find the last occurance of the original block id
    const copiesCount = repeatOfIds.filter((id) => id === originalItemId).length

    const originalItemClone = getClone(originalItem, type)
    originalItemClone.name = getName(originalItem.name, copiesCount ?? 1)
    originalItemClone.repeatable = false

    const newTemplate = insertItemAt(
      templateResponseData,
      type,
      originalItemClone,
      parentId,
      lastOccurance + 1
    )

    setTemplateData(newTemplate)
  }

  const onRemove = () => {
    const newTemplate = deleteItem(templateResponseData, type, item.id)

    setTemplateData(newTemplate)
  }

  return (
    <Wrapper>
      {showAddAnother && (
        <CaptureBlockActionButton
          size="small"
          onClick={onAddAnother}
          icon={<IconCopy size={16} />}
        >
          Repeat{' '}
          {type === 'BLOCK'
            ? 'field'
            : type === 'BLOCK_GROUP'
              ? 'group'
              : 'section'}
        </CaptureBlockActionButton>
      )}
      {showRemove && (
        <CaptureBlockActionButton
          negative
          size="small"
          shape="circle"
          onClick={onRemove}
          icon={<IconTrash size={12} />}
        />
      )}
    </Wrapper>
  )
}
